<template>
    <Modal2
        v-if="visible"
        id="content-form"
        design="v3"
        :show-title="false"
        :show-footer="false"
        @cancel="$emit('cancel')"
    >
        <div class="pcf-modal">
            <div class="pcf-header">
                <div class="pcf-header__title">{{ title }}</div>
            </div>

            <div v-if="errors.items.length && submitClicked" class="pcf-alert">
                {{ $t("settings.preparedContentRequiredAlert") }}
            </div>

            <form class="pcf-form" @submit.prevent="handleSubmit">

                <div class="pcf-section">
                    <div v-if="isChild" class="pcf-field">
                        <label>{{ $t("message.contentType") }}</label>
                        <select
                            v-model="content.type"
                            v-validate="'required'"
                            :data-vv-as="$t('message.contentType')"
                            :name="$t('message.contentType')"
                            :class="{ 'pcf-input-error': errors.has($t('message.contentType')) }"
                        >
                            <option :value="undefined">{{ $t("message.pickOneEllipsis") }}</option>
                            <option value="ALT">{{ isMac ? $t("message.preparedContentShortcutTypeCtrl") : $t("message.preparedContentShortcutTypeAlt") }}</option>
                            <option value="TAB">{{ $t("message.preparedContentShortcutTypeTab") }}</option>
                            <option value="HASH">{{ $t("message.preparedContentShortcutTypeHash") }}</option>
                            <option value="TITLE">{{ $t("message.title") }}</option>
                        </select>
                        <span v-if="errors.has($t('message.contentType'))" class="pcf-field-error">
                            {{ errors.first($t('message.contentType')) }}
                        </span>
                    </div>

                    <div v-if="showGroupSelect" class="pcf-field">
                        <label>{{ $t("message.group") }}</label>
                        <select v-model="content.parentId">
                            <option :value="null">{{ $t("message.pickOne") }}...</option>
                            <option v-for="opt in parentGroups" :key="opt.value" :value="opt.value">
                                {{ opt.text }}
                            </option>
                        </select>
                    </div>

                    <div v-if="isAlt" class="pcf-field">
                        <label>{{ $t("message.shortcutChar") }}</label>
                        <input
                            v-model="content.letter"
                            v-validate="'required|alpha_num|length:1|excluded:e,d,f,z,a,x,c,v,w,t,n'"
                            :data-vv-as="$t(content.letter)"
                            :name="$t('message.shortcutChar')"
                            :placeholder="$t('message.writeSomething')"
                            :class="{ 'pcf-input-error': errors.has($t('message.shortcutChar')) }"
                            autocomplete="off"
                            type="text"
                        />
                        <span v-if="errors.has($t('message.shortcutChar'))" class="pcf-field-error">
                            {{ errors.first($t('message.shortcutChar')) }}
                        </span>
                        <small v-else class="pcf-hint">{{ isMac ? $t("message.shortcutHintCtrl") : $t("message.shortcutHintAlt") }} {{ content.letter }}</small>
                    </div>

                    <div v-if="isTab" class="pcf-inline">
                        <div class="pcf-field">
                            <label>{{ $t("message.letterSequence") }}</label>
                            <input
                                v-model="content.letter"
                                v-validate="'required'"
                                :data-vv-as="$t('message.letterSequence')"
                                :name="$t('message.letterSequence')"
                                :placeholder="$t('message.exampleText')"
                                :class="{ 'pcf-input-error': errors.has($t('message.letterSequence')) }"
                                autocomplete="off"
                                type="text"
                            />
                            <span v-if="errors.has($t('message.letterSequence'))" class="pcf-field-error">
                                {{ errors.first($t('message.letterSequence')) }}
                            </span>
                        </div>
                        <div class="pcf-tab-divider">{{ $t("message.preparedContentTabDivider") }}</div>
                        <div class="pcf-field">
                            <label>{{ $t("message.shorcutKey") }}</label>
                            <input
                                v-model="content.number"
                                v-validate="'required|length:1'"
                                :data-vv-as="$t('message.shorcutKey')"
                                :name="$t('message.shorcutKey')"
                                :placeholder="$t('message.anyChar')"
                                :class="{ 'pcf-input-error': errors.has($t('message.shorcutKey')) }"
                                autocomplete="off"
                            />
                            <span v-if="errors.has($t('message.shorcutKey'))" class="pcf-field-error">
                                {{ errors.first($t('message.shorcutKey')) }}
                            </span>
                        </div>
                    </div>

                    <div v-if="isHash" class="pcf-field">
                        <label>{{ $t("message.hashText") }}</label>
                        <div class="pcf-prefix-input">
                            <span class="pcf-prefix-input__prefix">#</span>
                            <input
                                v-model="content.hash"
                                v-validate="'required|alpha_num'"
                                :data-vv-as="$t(content.hash)"
                                :name="$t('message.hashText')"
                                :placeholder="$t('message.exampleHash')"
                                :class="{ 'pcf-input-error': errors.has($t('message.hashText')) }"
                                autocomplete="off"
                                type="text"
                            />
                        </div>
                        <span v-if="errors.has($t('message.hashText'))" class="pcf-field-error">
                            {{ errors.first($t('message.hashText')) }}
                        </span>
                    </div>
                </div>

                <div class="pcf-section">
                    <div class="pcf-tabs-wrapper">
                        <div class="pcf-tabs">
                            <div
                                v-for="lang in systemLanguages"
                                :key="lang"
                                class="pcf-tab"
                                :class="{ 
                                    active: activeTab === lang,
                                    'pcf-tab--error': hasLangError(lang)
                                }"
                                @click="activeTab = lang"
                            >
                                {{ lang.toUpperCase() }}
                                <span v-if="hasLangError(lang)" class="pcf-tab__error-dot"></span>
                            </div>
                        </div>
                        <div class="pcf-tab-actions">
                            <div
                                class="pcf-icon-btn"
                                :class="{ 'pcf-icon-btn--loading': isTranslating }"
                                @click="!isTranslating && translateToOthers(activeTab)"
                                v-tippy="{ content: $t('message.vtippyTranslateButton') }"
                            >
                                <md-icon v-if="!isTranslating">translate</md-icon>
                                <md-icon v-else class="pcf-spinner">hourglass_empty</md-icon>
                            </div>
                            <div
                                class="pcf-icon-btn"
                                @click="clearOthers(activeTab)"
                                v-tippy="{ content: $t('message.vtippyTranslateClearButton') }"
                            >
                                <md-icon>clear</md-icon>
                            </div>
                            <div
                                class="pcf-icon-btn"
                                @click="showAddLangModal"
                                v-tippy="{ content: 'Yeni Dil Ekle' }"
                            >
                                <md-icon>add</md-icon>
                            </div>
                        </div>
                    </div>

                    <div class="pcf-tab-content">

                        <div class="pcf-field">
                            <label>{{ $t("message.titleContentForm") }}</label>
                            <input
                                v-model="contentByLang[activeTab].name"
                                :placeholder="$t('message.titleContentForm')"
                                type="text"
                                @blur="validateLang(activeTab)"
                            />
                        </div>

                        <div class="pcf-field">
                            <label>{{ $t("message.message") }}</label>
                            <quill-editor
                                ref="quillEditor"
                                v-model="contentByLang[activeTab].content"
                                :options="editorOptions"
                                class="pcf-editor"
                                @blur="validateLang(activeTab)"
                                @ready="onQuillReady"
                            />
                        </div>
                    </div>
                </div>

                <div class="pcf-footer">
                    <div class="pcf-status">
                        <label>{{ $t("report.status") }}</label>
                        <div class="pcf-toggle" @click="content.isActive = !content.isActive">
                            <div class="pcf-toggle__track" :class="{ active: content.isActive }">
                                <div class="pcf-toggle__thumb"></div>
                            </div>
                            <span class="pcf-toggle__label">
                                {{ content.isActive ? $t("message.active") : $t("message.passive") }}
                            </span>
                        </div>
                    </div>

                    <div class="pcf-actions">
                        <SupsisButton @click="$emit('cancel')">
                            {{ $t("message.cancel") }}
                        </SupsisButton>
                        <SupsisButton primary :disabled="loading" @click="handleSubmit">
                            <md-icon v-if="loading">hourglass_empty</md-icon>
                            <span v-else>{{ editMode ? $t("message.update") : $t("message.save") }}</span>
                        </SupsisButton>
                    </div>
                </div>
            </form>
        </div>

        <Modal2
            v-if="showLangModal"
            :show-footer="false"
            :title="$t('message.selectALanguage')"
            @cancel="showLangModal = false"
        >
            <div class="pcf-lang-modal">
                <div class="pcf-field">
                    <label>{{ $t("message.language") }}</label>
                    <select v-model="newLang">
                        <option :value="null">{{ $t("message.pickOne") }}...</option>
                        <option v-for="l in availableLanguages" :key="l.code" :value="l.code">
                            {{ l.label }}
                        </option>
                    </select>
                </div>
                <div class="pcf-lang-modal__actions">
                    <SupsisButton @click="showLangModal = false">{{ $t("message.cancel") }}</SupsisButton>
                    <SupsisButton primary :disabled="!newLang" @click="addLanguage">
                        {{ $t("message.save") }}
                    </SupsisButton>
                </div>
            </div>
        </Modal2>
    </Modal2>
</template>

<script>
import Modal2 from "../../../../../../components/common/Modal2.vue";
import SupsisButton from "../../../components/SupsisButton.vue";
import { isMac } from "../../../../../../utils/utils";
import { CONTENT_TYPES } from "../../../../../../utils/staticDatas";
import { PERMISSION_TYPES } from "../../../../../../language/permissionTypes";
import languages from "../../../../../../language/languages.json";
import request from "../../../../../../utils/request";
import { mapActions, mapState } from "vuex";
import { sendSocketMessage } from "../../../../../../utils/socketStream";
import "quill/dist/quill.snow.css";

export default {
    name: "PreparedContentFormModal",
    components: {
        Modal2,
        SupsisButton,
    },
    inject: {
        $validator: "$validator",
    },
    $_veeValidate: {
        validator: "parent",
    },
    props: {
        visible: Boolean,
        title: String,
        content: Object,
        isChild: Boolean,
        editMode: Boolean,
        loading: Boolean,
        submitClicked: Boolean,
        isMac: Boolean,
        contentsData: Array,
        getContentName: Function,
        canUpdate: Boolean,
    },
    data() {
        return {
            activeTab: null,
            showLangModal: false,
            newLang: null,
            isTranslating: false,
            imgCaches: {},
            contentByLang: {},
            langErrors: {},
            quillInstance: null,
            editorOptions: {
                theme: "snow",
                modules: {
                    toolbar: [
                        ["bold", "italic", "underline"],
                        [{ align: [] }],
                        [{ header: [1, 2, 3, false] }],
                        ["link", "code-block"],
                        [{ list: "ordered" }, { list: "bullet" }],
                        ["image"],
                    ],
                },
                placeholder: this.$t("message.writeSomething"),
            },
        };
    },
    computed: {
        ...mapState({
            systemLanguages: (state) => state.setting.settings.systemLanguages,
            site: (state) => state.site.site,
        }),
        isAlt() {
            return (this.content.type || "").toLowerCase() === "alt";
        },
        isTab() {
            return (this.content.type || "").toLowerCase() === "tab";
        },
        isHash() {
            return (this.content.type || "").toLowerCase() === "hash";
        },
        showGroupSelect() {
            return (this.content.type || "").toLowerCase() !== "title";
        },
        parentGroups() {
            return (this.contentsData || [])
                .filter((c) => c.parentId === null)
                .map((c) => ({
                    value: c._id,
                    text: this.getContentName(c),
                }));
        },
        availableLanguages() {
            return languages.filter((l) => !this.systemLanguages.includes(l.code));
        },
    },
    watch: {
        visible(val) {
            if (val) {
                this.syncContentByLang();
                if (this.systemLanguages.length) {
                    this.activeTab = this.systemLanguages[0];
                }
                this.langErrors = {};
            } else {
                this.resetModalState();
            }
        },
        contentByLang: {
            deep: true,
            handler(val) {
                if (!this._syncingFromContent) {
                    this.content.languageContent = { ...val };
                }
            },
        },
    },
    mounted() {
        this.syncContentByLang();
        if (this.systemLanguages.length) {
            this.activeTab = this.systemLanguages[0];
        }
    },
    methods: {
        onQuillReady(quill) {
            this.quillInstance = quill;
            this.setupLinkPreservation(quill);
        },
        setupLinkPreservation(quill) {
            if (!quill) return;
            
            let isProcessing = false;
            let linkHrefMap = new Map();
            
            quill.on('selection-change', (range) => {
                if (!range || range.length === 0) return;
                
                const formats = quill.getFormat(range);
                if (formats.link && formats.link.href) {
                    const key = `${range.index}-${range.length}`;
                    linkHrefMap.set(key, formats.link.href);
                }
            });
            
            quill.on('text-change', (delta, oldDelta, source) => {
                if (source !== 'user' || isProcessing) return;
                
                const selection = quill.getSelection();
                if (!selection || selection.length === 0) return;
                
                const formats = quill.getFormat(selection);
                
                if (formats.link && formats.link.href) {
                    const currentHref = formats.link.href;
                    const currentText = quill.getText(selection.index, selection.length);
                    
                    if (currentHref && currentText && currentText !== currentHref) {
                        isProcessing = true;
                        
                        this.$nextTick(() => {
                            try {
                                quill.formatText(selection.index, selection.length, 'link', currentHref, 'api');
                            } catch (e) {
                                console.error('Link preservation error:', e);
                            } finally {
                                isProcessing = false;
                            }
                        });
                    }
                } else {
                    for (const [key, href] of linkHrefMap.entries()) {
                        const [index, length] = key.split('-').map(Number);
                        const currentText = quill.getText(index, length);
                        
                        if (currentText && currentText.length > 0) {
                            const currentFormats = quill.getFormat(index, length);
                            
                            if (!currentFormats.link && href) {
                                isProcessing = true;
                                
                                this.$nextTick(() => {
                                    try {
                                        quill.formatText(index, length, 'link', href, 'api');
                                    } catch (e) {
                                        console.error('Link restoration error:', e);
                                    } finally {
                                        isProcessing = false;
                                    }
                                });
                                break;
                            }
                        }
                    }
                }
            });
        },
        ...mapActions({
            updateSettings: "setting/updateSettings",
            setSettingValue: "setting/setSettingValue",
        }),
        syncContentByLang() {
            this._syncingFromContent = true;
            const result = {};
            this.systemLanguages.forEach((lang) => {
                result[lang] = this.content.languageContent?.[lang] || { name: "", content: "" };
            });
            this.contentByLang = result;
            this.$nextTick(() => {
                this._syncingFromContent = false;
            });
        },
        async handleSubmit() {
            this.$emit('set-submit-clicked', true);
            
            await this.$nextTick();
            const isValid = await this.$validator.validateAll();
            
            if (!isValid) {
                this.$toasted.error("Lütfen tüm zorunlu alanları doldurun", {
                    duration: 2000,
                    position: "bottom-center",
                });
                return;
            }
            
            this.langErrors = {};
            let hasError = false;
            const emptyLangs = [];
            
            for (const lang of this.systemLanguages) {
                const isEmpty = !this.contentByLang[lang].name || !this.contentByLang[lang].content;
                if (isEmpty) {
                    this.langErrors[lang] = true;
                    emptyLangs.push(lang.toUpperCase());
                    hasError = true;
                }
            }
            
            if (hasError) {
                this.$toasted.error(`Lütfen tüm diller için içerik girin: ${emptyLangs.join(', ')}`, {
                    duration: 4000,
                    position: "bottom-center",
                });
                return;
            }
            
            this.$emit('submit');
        },
        validateLang(lang) {
            const isEmpty = !this.contentByLang[lang].name || !this.contentByLang[lang].content;
            if (isEmpty) {
                this.$set(this.langErrors, lang, true);
            } else {
                this.$set(this.langErrors, lang, false);
            }
        },
        hasLangError(lang) {
            return this.langErrors[lang] === true;
        },
        resetModalState() {
            this.langErrors = {};
            this.contentByLang = {};
            this.activeTab = null;
            this.showLangModal = false;
            this.newLang = null;
            this.isTranslating = false;
            this.imgCaches = {};
        },
        validateMultiContent() {
            return this.$validator.validateAll();
        },
        showAddLangModal() {
            this.newLang = null;
            this.showLangModal = true;
        },
        async addLanguage() {
            if (!this.newLang) return;
            const newSettings = this.setSettingValue({
                setting: "systemLanguages",
                value: [...this.systemLanguages, this.newLang],
            });
            if (newSettings.then) {
                await newSettings.then((x) => this.saveSettings(x));
            } else {
                await this.saveSettings(newSettings);
            }
            this.showLangModal = false;
        },
        async saveSettings(newSettings) {
            await this.updateSettings(newSettings);
            sendSocketMessage("admin settings-updated", {
                siteId: this.site._id,
            });
            this.$toasted.success(this.$t("message.settingsSaved"), {
                duration: 1000,
                position: "bottom-center",
            });
        },
        async translateToOthers(fromLang) {
            const sourceName = this.contentByLang[fromLang].name;
            const sourceContent = this.contentByLang[fromLang].content;

            if (!sourceName && !sourceContent) return;

            this.isTranslating = true;
            const targetLangs = this.systemLanguages.filter((l) => l !== fromLang);

            try {
                const results = await Promise.all(
                    targetLangs.map(async (toLang) => {
                        const nameResult = sourceName
                            ? await request.post("/translate", {
                                  data: sourceName,
                                  from: fromLang,
                                  to: toLang,
                              })
                            : null;
                        const contentResult = sourceContent
                            ? await request.post("/translate", {
                                  data: this.clearImages(sourceContent),
                                  from: fromLang,
                                  to: toLang,
                              })
                            : null;
                        return {
                            lang: toLang,
                            name: nameResult?.data || "",
                            content: contentResult?.data ? this.reWriteImages(contentResult.data) : "",
                        };
                    })
                );

                results.forEach((r) => {
                    this.contentByLang[r.lang].name = r.name;
                    this.contentByLang[r.lang].content = r.content;
                });

                this.$toasted.success("Çeviri tamamlandı", {
                    duration: 2000,
                    position: "bottom-center",
                });
            } catch (e) {
                this.$toasted.error("Çeviri başarısız oldu", {
                    duration: 2000,
                    position: "bottom-center",
                });
            }
            this.isTranslating = false;
        },
        clearOthers(exceptLang) {
            this.systemLanguages.forEach((lang) => {
                if (lang !== exceptLang) {
                    this.contentByLang[lang].name = "";
                    this.contentByLang[lang].content = "";
                }
            });
        },
        clearImages(htmlStr) {
            const parser = new DOMParser();
            const htmlDom = parser.parseFromString(htmlStr, "text/html");
            htmlDom.body.querySelectorAll("img").forEach((img, index) => {
                this.imgCaches[index] = img.src;
                img.src = "";
                img.setAttribute("cache-index", index.toString());
            });
            return htmlDom.body.innerHTML;
        },
        reWriteImages(htmlStr) {
            const parser = new DOMParser();
            const htmlDom = parser.parseFromString(htmlStr, "text/html");
            htmlDom.body.querySelectorAll("img").forEach((img) => {
                img.src = this.imgCaches[img.getAttribute("cache-index")];
            });
            return htmlDom.body.innerHTML;
        },
    },
};
</script>

<style lang="scss" scoped>
.pcf-modal {
    display: flex;
    flex-direction: column;
}

.pcf-header {
    display: flex;
    align-items: center;
    padding: 24px 40px;
}

.pcf-header__title {
    font-size: 20px;
    font-weight: 700;
    color: var(--color-text-strong-default);
    letter-spacing: -0.3px;
}

.pcf-alert {
    margin: 0 40px 20px;
    padding: 12px 16px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
    color: #ef4444;
    font-size: 13px;
    font-weight: 500;
}

.pcf-form {
    display: flex;
    flex-direction: column;
    padding: 32px 40px;
}

.pcf-section {
    margin-bottom: 32px;
}

.pcf-field {
    margin-bottom: 20px;

    label {
        display: block;
        font-size: 13px;
        font-weight: 600;
        color: var(--color-text-strong-default);
        margin-bottom: 8px;
    }

    input,
    select {
        width: 100%;
        padding: 10px 14px;
        border: 1px solid var(--color-border-default);
        border-radius: 8px;
        font-size: 14px;
        outline: none;
        transition: all 0.2s;
        background: #ffffff;

        &:focus {
            border-color: #22272d;
            box-shadow: 0 0 0 3px rgba(34, 39, 45, 0.1);
        }
    }
}

.pcf-hint {
    display: block;
    margin-top: 6px;
    font-size: 12px;
    color: var(--color-text-subtle);
    line-height: 1.5;
}

.pcf-input-error {
    border-color: #ef4444 !important;
    
    &:focus {
        border-color: #ef4444 !important;
        box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1) !important;
    }
}

.pcf-field-error {
    display: block;
    margin-top: 6px;
    font-size: 12px;
    color: #ef4444;
    line-height: 1.5;
    font-weight: 500;
}

.pcf-inline {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    gap: 16px;
    align-items: end;
    margin-bottom: 20px;
}

.pcf-tab-divider {
    padding: 10px 18px;
    background: var(--color-bg-neutral-default);
    border: 1px solid var(--color-border-default);
    border-radius: 8px;
    font-size: 12px;
    font-weight: 700;
    text-align: center;
    color: var(--color-text-default);
    margin-bottom: 20px;
}

.pcf-prefix-input {
    position: relative;
    display: flex;
    align-items: center;

    &__prefix {
        position: absolute;
        left: 12px;
        font-size: 14px;
        font-weight: 600;
        color: var(--color-text-default);
        pointer-events: none;
    }

    input {
        padding-left: 28px;
    }
}

.pcf-tabs-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--color-border-default);
    margin-bottom: 24px;
}

.pcf-tabs {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    overflow-x: auto;
    overflow-y: hidden;
    white-space: nowrap;
    scrollbar-width: thin;

    &::-webkit-scrollbar {
        height: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: var(--color-border-default);
        border-radius: 2px;
    }
}

.pcf-tab {
    padding: 10px 18px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    color: var(--color-text-subtle);
    background: transparent;
    border-radius: 6px;
    transition: all 0.2s;
    flex-shrink: 0;
    position: relative;
    border: 2px solid transparent;

    &.active {
        color: #5625CC;
        background: #EEE7FF;
    }

    &:hover:not(.active) {
        color: var(--color-text-default);
        background: rgba(34, 39, 45, 0.04);
    }
    
    &--error {
        border-color: #ef4444;
        
        &.active {
            background: #fee2e2;
            color: #ef4444;
        }
    }
}

.pcf-tab__error-dot {
    position: absolute;
    top: 6px;
    right: 6px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #ef4444;
}

.pcf-tab-content {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.pcf-tab-actions {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
}

.pcf-icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 1px solid var(--color-border-default);
    background: #ffffff;
    color: var(--color-text-default);
    cursor: pointer;
    transition: all 0.2s;

    .material-icons {
        font-size: 14px;
    }

    &:hover {
        background: var(--color-bg-neutral-default);
        border-color: #22272d;
        color: #22272d;
    }

    &:active {
        transform: scale(0.95);
    }

    &--loading {
        opacity: 0.6;
        cursor: wait;
        pointer-events: none;

        .pcf-spinner {
            animation: pcf-spin 1s linear infinite;
        }
    }
}

@keyframes pcf-spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

.pcf-editor {
    ::v-deep .ql-container {
        min-height: 220px;
        border-radius: 0 0 8px 8px;
        background: #ffffff;
    }

    ::v-deep .ql-toolbar {
        border-radius: 8px 8px 0 0;
        background: #ffffff;
    }

    ::v-deep .ql-editor {
        font-size: 14px;
        line-height: 1.6;
    }
}

.pcf-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 24px 40px;
    border-top: 1px solid var(--color-border-default);
}

.pcf-status {
    display: flex;
    align-items: center;
    gap: 16px;

    label {
        font-size: 13px;
        font-weight: 600;
        color: var(--color-text-strong-default);
    }
}

.pcf-toggle {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    user-select: none;

    &__track {
        position: relative;
        width: 44px;
        height: 24px;
        border-radius: 12px;
        background: #d1d5db;
        transition: background 0.3s;

        &.active {
            background: #5625CC;
        }
    }

    &__thumb {
        position: absolute;
        top: 2px;
        left: 2px;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: #ffffff;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        transition: transform 0.3s;

        .pcf-toggle__track.active & {
            transform: translateX(20px);
        }
    }

    &__label {
        font-size: 13px;
        font-weight: 600;
        color: var(--color-text-default);
    }
}

.pcf-actions {
    display: flex;
    gap: 10px;
}

.pcf-lang-modal {
    padding: 20px;

    &__actions {
        display: flex;
        gap: 8px;
        justify-content: flex-end;
        margin-top: 16px;
    }
}
</style>

<style lang="scss">
#content-form {
    .ant-modal {
        width: 720px !important;
        max-width: 90vw;
    }

    .ant-modal-body {
        padding: 0 !important;
    }
}
</style>
